import type { DebugFrame } from "fast-2d/debug";
import type { Camera } from "./Camera";

const BODY_TRANSFORM_STRIDE = 6;
const AABB_STRIDE = 5;
// Manifold stride 10: bodyA, bodyB, partA, partB, nx, ny, pointOffset, pointCount, sensor, key.
const CONTACT_MANIFOLD_STRIDE = 10;
const CONTACT_POINT_STRIDE = 7;
const PAIR_STRIDE = 2;
const JOINT_SEGMENT_STRIDE = 8;
const BODY_ISLAND_STRIDE = 3;
const BODY_FLAG_STATIC = 1;
const BODY_FLAG_SLEEPING = 2;
const BODY_FLAG_SENSOR = 4;
const ISLAND_STATE_SLEEPING = 2;

export function drawDebugFrame(
	frame: DebugFrame,
	ctx: CanvasRenderingContext2D,
	cam: Camera,
): void {
	ctx.save();
	drawIslandTints(frame, ctx, cam);
	drawBroadphasePairs(frame, ctx, cam);
	drawAabbs(frame, ctx, cam);
	drawJoints(frame, ctx, cam);
	drawContacts(frame, ctx, cam);
	ctx.restore();
}

function drawIslandTints(frame: DebugFrame, ctx: CanvasRenderingContext2D, cam: Camera): void {
	if (frame.bodyCount === 0) return;
	ctx.lineWidth = 1;
	for (let i = 0; i < frame.bodyCount; i++) {
		const bodyId = frame.bodyIds[i]!;
		const islandOffset = findIslandOffset(frame, bodyId);
		const aabbOffset = findAabbOffset(frame, bodyId);
		if (islandOffset < 0 || aabbOffset < 0) continue;

		const bodyFlags = frame.bodyFlags[i]!;
		const islandId = frame.bodyIslands[islandOffset + 1]!;
		const islandState = frame.bodyIslands[islandOffset + 2]!;
		const hue = islandId >= 0 ? (islandId * 73) % 360 : 215;
		const alpha = bodyFlags & BODY_FLAG_STATIC ? 0.05 : islandState === ISLAND_STATE_SLEEPING ? 0.1 : 0.07;
		ctx.fillStyle = `hsla(${hue}, 82%, 58%, ${alpha})`;
		ctx.strokeStyle = `hsla(${hue}, 82%, 68%, ${alpha + 0.1})`;
		strokeAndFillAabb(frame, ctx, cam, aabbOffset, true);
	}
}

function drawAabbs(frame: DebugFrame, ctx: CanvasRenderingContext2D, cam: Camera): void {
	ctx.lineWidth = 1;
	for (let i = 0; i < frame.aabbCount; i++) {
		const offset = i * AABB_STRIDE;
		const bodyId = frame.aabbs[offset]!;
		const bodyIndex = findBodyIndex(frame, bodyId);
		const flags = bodyIndex >= 0 ? frame.bodyFlags[bodyIndex]! : 0;
		ctx.strokeStyle = flags & BODY_FLAG_SLEEPING
			? "rgba(132, 156, 190, 0.34)"
			: flags & BODY_FLAG_SENSOR
				? "rgba(250, 211, 100, 0.55)"
				: "rgba(124, 214, 255, 0.42)";
		strokeAndFillAabb(frame, ctx, cam, offset, false);
	}
}

function drawBroadphasePairs(frame: DebugFrame, ctx: CanvasRenderingContext2D, cam: Camera): void {
	if (frame.pairCount === 0) return;
	ctx.lineWidth = 1;
	ctx.strokeStyle = "rgba(255, 215, 102, 0.16)";
	ctx.beginPath();
	for (let i = 0; i < frame.pairCount; i++) {
		const offset = i * PAIR_STRIDE;
		const a = findTransformOffset(frame, frame.pairs[offset]!);
		const b = findTransformOffset(frame, frame.pairs[offset + 1]!);
		if (a < 0 || b < 0) continue;
		ctx.moveTo(cam.worldToScreenX(frame.bodyTransforms[a]!), cam.worldToScreenY(frame.bodyTransforms[a + 1]!));
		ctx.lineTo(cam.worldToScreenX(frame.bodyTransforms[b]!), cam.worldToScreenY(frame.bodyTransforms[b + 1]!));
	}
	ctx.stroke();
}

function drawJoints(frame: DebugFrame, ctx: CanvasRenderingContext2D, cam: Camera): void {
	if (frame.jointSegmentCount === 0) return;
	ctx.lineWidth = 2;
	for (let i = 0; i < frame.jointSegmentCount; i++) {
		const offset = i * JOINT_SEGMENT_STRIDE;
		const type = frame.jointSegments[offset + 1]!;
		const ax = frame.jointSegments[offset + 4]!;
		const ay = frame.jointSegments[offset + 5]!;
		const bx = frame.jointSegments[offset + 6]!;
		const by = frame.jointSegments[offset + 7]!;
		ctx.strokeStyle = type === 2 ? "rgba(255, 196, 87, 0.92)" : "rgba(120, 200, 255, 0.82)";
		ctx.fillStyle = ctx.strokeStyle;
		ctx.beginPath();
		ctx.moveTo(cam.worldToScreenX(ax), cam.worldToScreenY(ay));
		ctx.lineTo(cam.worldToScreenX(bx), cam.worldToScreenY(by));
		ctx.stroke();
		dot(ctx, cam, ax, ay, 3.2);
		dot(ctx, cam, bx, by, 3.2);
	}
}

function drawContacts(frame: DebugFrame, ctx: CanvasRenderingContext2D, cam: Camera): void {
	if (frame.contactCount === 0) return;
	for (let i = 0; i < frame.contactCount; i++) {
		const manifoldOffset = i * CONTACT_MANIFOLD_STRIDE;
		const nx = frame.contactManifolds[manifoldOffset + 4]!;
		const ny = frame.contactManifolds[manifoldOffset + 5]!;
		const pointOffset = frame.contactManifolds[manifoldOffset + 6]!;
		const pointCount = frame.contactManifolds[manifoldOffset + 7]!;
		const sensor = frame.contactManifolds[manifoldOffset + 8]! === 1;
		for (let j = 0; j < pointCount; j++) {
			const offset = (pointOffset + j) * CONTACT_POINT_STRIDE;
			const x = frame.contactPoints[offset]!;
			const y = frame.contactPoints[offset + 1]!;
			const penetration = frame.contactPoints[offset + 2]!;
			ctx.fillStyle = sensor ? "rgba(250, 211, 100, 0.95)" : "rgba(22, 240, 180, 0.96)";
			dot(ctx, cam, x, y, 3.5);

			ctx.lineWidth = 2;
			ctx.strokeStyle = sensor ? "rgba(250, 211, 100, 0.72)" : "rgba(22, 240, 180, 0.78)";
			ctx.beginPath();
			ctx.moveTo(cam.worldToScreenX(x), cam.worldToScreenY(y));
			ctx.lineTo(cam.worldToScreenX(x + nx * 0.42), cam.worldToScreenY(y + ny * 0.42));
			ctx.stroke();

			ctx.lineWidth = 1.5;
			ctx.strokeStyle = penetration >= 0 ? "rgba(255, 107, 107, 0.82)" : "rgba(124, 214, 255, 0.62)";
			const tick = Math.min(0.3, Math.max(0.06, Math.abs(penetration) * 2));
			ctx.beginPath();
			ctx.moveTo(cam.worldToScreenX(x - ny * tick), cam.worldToScreenY(y + nx * tick));
			ctx.lineTo(cam.worldToScreenX(x + ny * tick), cam.worldToScreenY(y - nx * tick));
			ctx.stroke();
		}
	}
}

function strokeAndFillAabb(
	frame: DebugFrame,
	ctx: CanvasRenderingContext2D,
	cam: Camera,
	offset: number,
	fill: boolean,
): void {
	const minX = frame.aabbs[offset + 1]!;
	const minY = frame.aabbs[offset + 2]!;
	const maxX = frame.aabbs[offset + 3]!;
	const maxY = frame.aabbs[offset + 4]!;
	const x = cam.worldToScreenX(minX);
	const y = cam.worldToScreenY(minY);
	const w = (maxX - minX) * cam.scale;
	const h = (maxY - minY) * cam.scale;
	if (fill) ctx.fillRect(x, y, w, h);
	ctx.strokeRect(x, y, w, h);
}

function dot(ctx: CanvasRenderingContext2D, cam: Camera, x: number, y: number, r: number): void {
	ctx.beginPath();
	ctx.arc(cam.worldToScreenX(x), cam.worldToScreenY(y), r, 0, Math.PI * 2);
	ctx.fill();
}

function findBodyIndex(frame: DebugFrame, bodyId: number): number {
	for (let i = 0; i < frame.bodyCount; i++) {
		if (frame.bodyIds[i] === bodyId) return i;
	}
	return -1;
}

function findTransformOffset(frame: DebugFrame, bodyId: number): number {
	const index = findBodyIndex(frame, bodyId);
	return index >= 0 ? index * BODY_TRANSFORM_STRIDE : -1;
}

function findAabbOffset(frame: DebugFrame, bodyId: number): number {
	for (let i = 0; i < frame.aabbCount; i++) {
		const offset = i * AABB_STRIDE;
		if (frame.aabbs[offset] === bodyId) return offset;
	}
	return -1;
}

function findIslandOffset(frame: DebugFrame, bodyId: number): number {
	for (let i = 0; i < frame.bodyCount; i++) {
		const offset = i * BODY_ISLAND_STRIDE;
		if (frame.bodyIslands[offset] === bodyId) return offset;
	}
	return -1;
}